import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { downloadBlob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, euro, fmtDate, EmptyState } from "@/components/shared";
import MultiFilter from "@/components/MultiFilter";
import { Wrench, AlertTriangle, FileDown, MailCheck, CalendarRange, Package, ChevronsUpDown, ChevronUp, ChevronDown } from "lucide-react";

const emptyFilters = { date: [], asset_name: [], activity_type_name: [], activities: [], supplier: [], invoice_number: [], invoice_date: [], invoice_amount: [] };

export default function Maintenance() {
  const [records, setRecords] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const [filters, setFilters] = useState(emptyFilters);
  const [assetFilter, setAssetFilter] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/maintenance").then(({ data }) => setRecords(data));
    api.get("/maintenance/alerts").then(({ data }) => setAlerts(data));
  }, []);

  const totalSpend = records.reduce((s, r) => s + (r.invoice_amount || 0), 0);

  const sendReminders = async () => {
    try {
      const { data } = await api.post("/maintenance/send-reminders");
      if (data.disabled) toast.info("Gli avvisi email sono disattivati nelle Impostazioni");
      else toast.success(`Promemoria inviati: ${data.sent}`);
    } catch {
      toast.error("Invio promemoria fallito");
    }
  };

  const val = {
    date: (r) => fmtDate(r.date) || "—",
    asset_name: (r) => r.asset_name || "—",
    activity_type_name: (r) => r.activity_type_name || "—",
    activities: (r) => r.activities || "—",
    supplier: (r) => r.supplier || "—",
    invoice_number: (r) => r.invoice_number || "—",
    invoice_date: (r) => fmtDate(r.invoice_date) || "—",
    invoice_amount: (r) => euro(r.invoice_amount),
  };

  const opt = (fn) => [...new Set(records.map(fn))].filter(Boolean).sort();
  const assetOptions = opt((r) => r.asset_name || "—");
  const matchFilter = (arr, v) => arr.length === 0 || arr.includes(v);

  const sortVal = (r) => {
    switch (sort.key) {
      case "date": return r.date || "";
      case "invoice_date": return r.invoice_date || "";
      case "invoice_amount": return r.invoice_amount || 0;
      default: return (val[sort.key]?.(r) || "").toString().toLowerCase();
    }
  };

  const filtered = records
    .filter((r) => {
      if (assetFilter.length && !assetFilter.includes(r.asset_name || "—")) return false;
      if (dateFrom && (r.date || "") < dateFrom) return false;
      if (dateTo && (r.date || "") > dateTo) return false;
      return Object.keys(emptyFilters).every((k) => matchFilter(filters[k], val[k](r)));
    })
    .sort((x, y) => {
      const vx = sortVal(x), vy = sortVal(y);
      const cmp = vx < vy ? -1 : vx > vy ? 1 : 0;
      return sort.dir === "asc" ? cmp : -cmp;
    });

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
  const SortIcon = ({ k }) => sort.key !== k ? <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" /> : sort.dir === "asc" ? <ChevronUp className="h-3.5 w-3.5 text-primary" /> : <ChevronDown className="h-3.5 w-3.5 text-primary" />;
  const Th = ({ k, children, filter, className = "", align = "center" }) => (
    <TableHead className={`label-caps ${className}`}>
      <div className={`flex items-center gap-1 ${align === "start" ? "justify-start" : "justify-center"}`}>
        <button className="flex items-center gap-1 hover:text-primary" onClick={() => toggleSort(k)} data-testid={`sort-${k}`}>
          {children} <SortIcon k={k} />
        </button>
        {filter}
      </div>
    </TableHead>
  );
  const colFilter = (k, label) => (
    <MultiFilter label={label} options={opt(val[k])} value={filters[k]} onChange={(v) => setFilters({ ...filters, [k]: v })} testid={`filter-${k}`} />
  );

  const dateRangeActive = dateFrom || dateTo;

  return (
    <div>
      <PageHeader eyebrow="Interventi" title="Attività" subtitle="Registro di tutte le attività eseguite e scadenze programmate">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" data-testid="filter-mezzi-toolbar" className={`font-semibold ${assetFilter.length ? "border-primary text-primary" : ""}`}>
              <Package className="h-4 w-4 mr-1.5" /> Asset{assetFilter.length ? ` (${assetFilter.length})` : ""}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-3" align="end">
            <div className="label-caps text-muted-foreground mb-2">Filtra per bene</div>
            <div className="flex gap-2 mb-2">
              <Button variant="outline" size="sm" className="h-7 text-xs flex-1" onClick={() => setAssetFilter([...assetOptions])} data-testid="filter-mezzi-toolbar-all">Seleziona tutto</Button>
              <Button variant="outline" size="sm" className="h-7 text-xs flex-1" onClick={() => setAssetFilter([])} data-testid="filter-mezzi-toolbar-none">Deseleziona</Button>
            </div>
            <div className="max-h-60 overflow-y-auto space-y-1.5">
              {assetOptions.length ? assetOptions.map((opt) => (
                <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer py-0.5">
                  <Checkbox checked={assetFilter.includes(opt)} onCheckedChange={() => setAssetFilter(assetFilter.includes(opt) ? assetFilter.filter((v) => v !== opt) : [...assetFilter, opt])} />
                  <span className="truncate">{opt}</span>
                </label>
              )) : <p className="text-sm text-muted-foreground">Nessun bene disponibile.</p>}
            </div>
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" data-testid="filter-date-range-button" className={`font-semibold ${dateRangeActive ? "border-primary text-primary" : ""}`}>
              <CalendarRange className="h-4 w-4 mr-1.5" /> Data osservazione
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-3" align="end">
            <div className="label-caps text-muted-foreground mb-2">Intervallo data</div>
            <div className="space-y-2">
              <div className="space-y-1">
                <Label className="text-xs">Da</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">A</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
              </div>
              <Button variant="ghost" size="sm" className="w-full h-7 text-xs" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="filter-date-clear">Azzera</Button>
            </div>
          </PopoverContent>
        </Popover>
        <Button variant="outline" onClick={() => downloadBlob("/export/maintenance", "manutenzioni.xlsx")} data-testid="export-maintenance-button" className="font-semibold">
          <FileDown className="h-4 w-4 mr-1.5" /> Esporta XLSX
        </Button>
        {isAdmin && (
          <Button onClick={sendReminders} data-testid="send-reminders-button" className="font-semibold">
            <MailCheck className="h-4 w-4 mr-1.5" /> Invia promemoria
          </Button>
        )}
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <Card className="p-6 rounded-lg">
          <div className="label-caps text-muted-foreground">Interventi</div>
          <div className="stat-number text-4xl mt-3">{records.length}</div>
        </Card>
        <Card className="p-6 rounded-lg">
          <div className="label-caps text-muted-foreground">Spesa totale</div>
          <div className="stat-number text-4xl mt-3">{euro(totalSpend)}</div>
        </Card>
        <Card className="p-6 rounded-lg col-span-2 lg:col-span-1">
          <div className="label-caps text-muted-foreground">Avvisi scadenze</div>
          <div className="stat-number text-4xl mt-3 text-warning">{alerts.length}</div>
        </Card>
      </div>

      <Tabs defaultValue="registro">
        <TabsList>
          <TabsTrigger value="registro" data-testid="tab-registro">Registro interventi</TabsTrigger>
          <TabsTrigger value="scadenze" data-testid="tab-scadenze">Scadenze ({alerts.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="registro" className="mt-4">
          <Card className="rounded-lg overflow-hidden">
            {filtered.length ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                      <Th k="date" filter={colFilter("date", "Data")}>Data</Th>
                      <Th k="asset_name" align="start" filter={colFilter("asset_name", "Bene")}>Bene</Th>
                      <Th k="activity_type_name" className="hidden sm:table-cell" filter={colFilter("activity_type_name", "Tipo")}>Tipo</Th>
                      <Th k="activities" align="start" className="hidden md:table-cell" filter={colFilter("activities", "Attività")}>Attività</Th>
                      <Th k="supplier" align="start" className="hidden sm:table-cell" filter={colFilter("supplier", "Fornitore")}>Fornitore</Th>
                      <Th k="invoice_number" className="hidden lg:table-cell" filter={colFilter("invoice_number", "Numero fattura")}>N. fattura</Th>
                      <Th k="invoice_date" className="hidden lg:table-cell" filter={colFilter("invoice_date", "Data fattura")}>Data fattura</Th>
                      <Th k="invoice_amount" className="text-right" filter={colFilter("invoice_amount", "Importo")}>Importo</Th>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((r) => (
                      <TableRow key={r.id} data-testid={`maint-row-${r.id}`} className="cursor-pointer" onClick={() => navigate(`/beni/${r.asset_id}`)}>
                        <TableCell className="text-muted-foreground whitespace-nowrap text-center">{fmtDate(r.date)}</TableCell>
                        <TableCell className="font-semibold text-left">{r.asset_name}</TableCell>
                        <TableCell className="hidden sm:table-cell text-muted-foreground text-center">{r.activity_type_name || "—"}</TableCell>
                        <TableCell className="hidden md:table-cell max-w-xs truncate text-left">{r.activities}</TableCell>
                        <TableCell className="hidden sm:table-cell text-muted-foreground text-left">{r.supplier || "—"}</TableCell>
                        <TableCell className="hidden lg:table-cell font-mono text-sm text-center">{r.invoice_number || "—"}</TableCell>
                        <TableCell className="hidden lg:table-cell text-muted-foreground text-center whitespace-nowrap">{r.invoice_date ? fmtDate(r.invoice_date) : "—"}</TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">{euro(r.invoice_amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <EmptyState icon={Wrench} title="Nessun intervento" subtitle="Registra le manutenzioni dalla scheda di ciascun bene o modifica i filtri." />
            )}
          </Card>
        </TabsContent>

        <TabsContent value="scadenze" className="mt-4">
          <Card className="rounded-lg overflow-hidden">
            {alerts.length ? (
              <div className="divide-y divide-border">
                {alerts.map((a) => (
                  <div key={a.asset_id} data-testid={`scadenza-${a.asset_id}`} onClick={() => navigate(`/beni/${a.asset_id}`)}
                    className="flex items-center justify-between p-4 hover:bg-secondary/40 cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className={`h-9 w-9 rounded-md flex items-center justify-center ${a.status === "scaduta" ? "bg-destructive/10 text-destructive" : "bg-warning/12 text-warning"}`}>
                        <AlertTriangle className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="font-semibold">{a.asset_name} <span className="text-muted-foreground font-normal">· {a.activity_type_name}</span></div>
                        <div className="text-xs text-muted-foreground">Scadenza {fmtDate(a.next_due_date)}{a.next_due_km_hours ? ` · ${a.next_due_km_hours} ${a.tracking_type === "km" ? "km" : "ore"}` : ""}</div>
                      </div>
                    </div>
                    <span className={`label-caps px-2.5 py-1 rounded-md ${a.status === "scaduta" ? "bg-destructive/12 text-destructive" : "bg-warning/12 text-warning"}`}>
                      {a.status === "scaduta" ? `Scaduta ${Math.abs(a.days)}g fa` : `Tra ${a.days}g`}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState icon={AlertTriangle} title="Nessuna scadenza imminente" subtitle="Le prossime manutenzioni programmate compariranno qui." />
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
